/**
 * Mission Config API Client
 *
 * GW-5448 (P2-3): Thin wrapper around `GET /v1/missions/config` exposed by
 * the gw-missions Lambda. Lives at the same host as the existing mission
 * routes (driven by `VITE_MISSION_LAMBDA_URL`), so we reuse that base-URL
 * resolution pattern rather than routing through the gw-api BaseApiClient.
 *
 * Contract: gw-missions/docs/designs/2026-04-30-missions-ui-phase2-endpoints.md
 */

import { getAuthToken } from '@/lib/devStackAuth.util';
import type { MissionConfigPayload } from '@/types/missionConfig.types';

export class MissionConfigApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'MissionConfigApiError';
  }
}

export class MissionConfigApiClient {
  // getToken must return the Cognito ACCESS token (not id-token).
  // Backend uses tokenUse: 'access'.
  constructor(
    private readonly baseUrl: string,
    private readonly getToken: () => Promise<string>
  ) {}

  async getConfig(): Promise<MissionConfigPayload> {
    const token = await this.getToken();
    const res = await fetch(`${this.baseUrl}/v1/missions/config`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const body = await res.text();
      throw new MissionConfigApiError(res.status, body || `HTTP ${res.status}`);
    }
    return (await res.json()) as MissionConfigPayload;
  }
}

export function createMissionConfigClient(
  getToken: () => Promise<string>
): MissionConfigApiClient | null {
  const base = import.meta.env.VITE_MISSION_LAMBDA_URL as string | undefined;
  if (!base) return null;
  return new MissionConfigApiClient(base, getToken);
}

/**
 * Lazy singleton used by the Zustand slice and other call sites that cannot
 * thread an explicit client through hook state. Defers base-URL resolution
 * until the first call so Vite env-vars stubbed by tests are respected.
 */
class MissionConfigApiSingleton {
  async getConfig(): Promise<MissionConfigPayload> {
    const client = createMissionConfigClient(async () => {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');
      return token;
    });
    if (!client) {
      throw new Error(
        'VITE_MISSION_LAMBDA_URL is not configured; mission config endpoint is unavailable'
      );
    }
    return client.getConfig();
  }
}

export const missionConfigApi = new MissionConfigApiSingleton();
